import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Box, Tabs, Tab, Button, IconButton, Switch } from "@mui/material";
import TeamList from "../../features/manageMaintenanceTeam/teamList";
import TeamTopbar from "../../features/manageMaintenanceTeam/topbar";
import {
  useLazyCustomerExportQuery,
  useLazyDeleteCustomerQuery,
  useLazyGetCustomersQuery,
  useUpdateStatusCustomerMutation,
} from "../../services/customer";
import { Customer } from "../../types/General";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import { isValidInput } from "../../utils/validations";
import SearchBar from "../../components/SearchBar";
import { GridColDef } from "@mui/x-data-grid";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import TableComponent from "../../components/TableComponent";
import usePermission from "../../hooks/usePermission";
import { ROLES } from "../../utils/enums";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageMaintenanceTeam = () => {
  const navigate = useNavigate();

  const [getCustomers] = useLazyGetCustomersQuery();
  const { isAdd, isDelete, isView } = usePermission({
    label: "Manage Maintenance Team",
    page_permission: "isView",
  });
  const [updateStatus] = useUpdateStatusCustomerMutation();
  const [deleteCustomer] = useLazyDeleteCustomerQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState<number>(1);
  const [exportCsv] = useLazyCustomerExportQuery();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [rows, setRows] = useState<Customer[]>([]);
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [openDelete, setOpenDelete] = useState(false);

  const [valueTabs, setValueTabs] = React.useState(0);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };
  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };
  const label = { inputProps: { "aria-label": "Switch demo" } };

  const handleStatusUpdate = async (id: string, status: boolean) => {
    const body = {
      isBlocked: status,
      userId: id,
    };

    try {
      const response = await updateStatus(body).unwrap();
      if (response?.statusCode === 200) {
        const updatedData = rows?.map((item) => {
          if (item?._id === id) {
            return {
              ...item,
              isBlocked: status,
            };
          }
          return item;
        });
        setRows(updatedData);
        if (status === true) {
          showToast("Management team deactivated successfully");
        } else {
          showToast("Management team activated successfully");
        }
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 260,
      field: "name",
      headerName: "Team Name",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            className="user_block click_action"
            onClick={() =>
              navigate(`/manage-maintenance-team/detail/${row?._id}`)
            }
          >
            <figure>
              <img
                src={row?.image || "/static/images/user_placeholder.png"}
                alt=""
              />
            </figure>
            <p>
              <b>{row?.fullName}</b> <span>{row?.email}</span>
            </p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 200,
      field: "profession",
      headerName: "Profession/Skills",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <p>
            {row?.profession
              ?.map((item: any) => item?.categoryName)
              .join(", ") || "-"}
          </p>
        );
      },
    },
    {
      minWidth: 150,
      field: "ongoingJobs",
      headerName: "Ongoing Jobs",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return <p>NA</p>;
      },
    },
    {
      minWidth: 150,
      field: "completedJobs",
      headerName: "Completed Jobs",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return <p>NA</p>;
      },
    },

    {
      minWidth: 150,
      field: "earnings",
      headerName: "Total Earnings",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <p className="c_success">
            <strong>AED {row?.totalEarning || "0"}</strong>
          </p>
        );
      },
    },
    {
      minWidth: 100,
      field: "status",
      headerName: "Status",
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <Switch
            {...label}
            size="small"
            checked={!row?.isBlocked}
            onChange={() => handleStatusUpdate(row?._id, !row?.isBlocked)}
          />
        );
      },
    },
    {
      minWidth: 130,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions v2">
            <Box>
              <IconButton
                onClick={() =>
                  navigate(`/manage-maintenance-team/detail/${row?._id}`)
                }
              >
                <img src="/static/images/eye_icon.svg" alt="" />
              </IconButton>
            </Box>
            {isAdd && (
              <Box>
                <IconButton
                  onClick={() =>
                    navigate(`/manage-maintenance-team/edit/${row?._id}`)
                  }
                >
                  <img src="/static/images/edit2_icon.svg" alt="" />
                </IconButton>
              </Box>
            )}
            {isDelete && (
              <Box>
                <IconButton
                  onClick={() => {
                    setSelectedId(row?._id);
                    setOpenDelete(true);
                  }}
                >
                  <img src="/static/images/trash2_icon.svg" alt="" />
                </IconButton>
              </Box>
            )}
          </div>
        );
      },
    },
  ];

  const getAllMaintenance = async () => {
    setIsLoading(true);
    try {
      const response = await getCustomers({
        page,
        limit: 10,
        search: debouncedSearchTerm.trim(),
        role: 4,
        status: valueTabs + 1,
      }).unwrap();
      if (response?.statusCode === 200) {
        setIsLoading(false);

        setRows(response?.data?.list || []);

        setCount(response?.data?.totalCount);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    const body = {
      userId: selectedId || "",
    };
    try {
      const response = await deleteCustomer(body).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast("Deleted successfully");
        getAllMaintenance();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

   const getExportCsv = async () => {
      try {
        const response = await exportCsv({
          page,
          limit: 10,
          search: debouncedSearchTerm.trim(),
          role: 4,
          status: valueTabs + 1,
        }).unwrap();
        if (response?.statusCode === 200) {
          window.open(response?.data || "");
        }
      } catch (error) {
        setIsLoading(false);
      }
    };

  useEffect(() => {
    getAllMaintenance();
  }, [debouncedSearchTerm, page, valueTabs]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Manage Maintenance Team</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Manage
          Maintenance Team
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Approved Team" {...a11yProps(0)} />
              <Tab label="Pending Team" {...a11yProps(1)} />
              <Tab label="Rejected Team" {...a11yProps(2)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        {/* <TeamTopbar /> */}

        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder="Search..."
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
          <div className="right_s">
            {isAdd && (
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-maintenance-team/add")}
              >
                <AddIcon /> Add
              </Button>
            )}
            {rows.length !==0 &&<Button className="btn btn_grey" onClick={getExportCsv}>
              <DownloadIcon /> Export
            </Button>}
          </div>
        </div>

        <CustomTabPanel value={valueTabs} index={0}>
          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={rows}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={isLoading}
            />
          </div>
          {/* <TeamList /> */}
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={rows}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={isLoading}
            />
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={2}>
          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={rows}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={isLoading}
            />
          </div>
        </CustomTabPanel>
      </div>
      <ConfirmationModal
        text="Are you sure to delete this customer"
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
      />
    </MainContainer>
  );
};

export default ManageMaintenanceTeam;
